import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getRetrieveVehicleKey } from '.';
import { vehicleService } from '../../../application/VehicleService';
import * as routing from '../../../constants/Routing';
import { DriversProject } from '../../../domain/driversProject/DriversProject';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import { useIsLogged } from '../../../hooks/session/useIsLogged';
import { useErrorMiddleware } from '../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../hooks/useShowSnackbarMessage';

export type UseRetrieveVehicleData =
  | {
      vehicle: Vehicle;
      project: DriversProject;
    }
  | undefined;

type UseRetrieveVehicleResponseProps = ServiceResponse<Function, UseRetrieveVehicleData>;

// Hooks act as 'Adapter' layer.
export const useRetrieveVehicle = (): UseRetrieveVehicleResponseProps => {
  const navigate = useNavigate();
  const { isLogged } = useIsLogged();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { data, isError, isLoading, isSuccess, refetch } = useQuery(
    getRetrieveVehicleKey(),
    () => vehicleService.getVehicle(),
    {
      retry: 1,
      enabled: Boolean(isLogged),
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      onError: (err: any) => {
        console.info(`Retrieve Vehicle Error: `, err?.response);

        if (err?.response?.data?.messageId === `highway.subscription.error.expired_subscription`) {
          navigate(routing.SUBSCRIPTION_EXPIRED);
        } else {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    data: data
      ? {
          vehicle: data.vehicle,
          project: data.project,
        }
      : undefined,
    run: refetch,
  };
};
